import { OLD_TAB_TO_GROUP } from "../../ui/editor/sections/groups";
import { groupOfSection as groupOf } from "../../ui/editor/sections/groupIndex";
import { RETIRED_BASIC_PANELS } from "../../ui/editor/rails/railConfig";

/**
 * The `ui` domain — purely host-side editor chrome state (which panel/tab is
 * open, collapsed sections, grid view mode, selection-follow). Persisted to
 * localStorage so the editor reopens exactly as the user left it. Panel/tab ids
 * are kept as plain strings here to avoid a state→ui import cycle; the UI tree
 * narrows them to its TabId / LeftPanelId unions.
 */
export interface UiState {
  /** Bumped when a field's saved value must not survive a reload. See loadUi. */
  schema: number;
  /** Open left library panel id, or null when the left panel is collapsed. */
  leftPanel: string | null;
  /** Open right inspector tab id, or null when the right panel is collapsed. */
  rightTab: string | null;
  /** What the rail chevron re-opens after a collapse. */
  lastLeftPanel: string | null;
  lastRightTab: string | null;
  /** `${tabId}.${sectionId}` → collapsed? (absent = use section default). */
  collapsed: Record<string, boolean>;
  /**
   * v60 — the split viewport. Persisted, because reopening the editor in the
   * layout you left it in is the whole point of this slice; the viewer boots in
   * `single` and the host re-asserts `quad` once the RPC handshake resolves.
   */
  viewportLayout: "single" | "quad";
  /** Which pane the per-pane switches consider current. Follows viewer clicks. */
  activePane: "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
  /**
   * Per-pane mode. NOT persisted — the split view always opens X / Y / Z / 3D.
   * Remembering unlocked panes across reloads meant a layout could come back
   * showing "3D" in three panes with no way to tell why. The switches still work
   * for the session.
   */
  paneModes: Record<string, "x" | "y" | "z" | "perspective">;
  /** Library grid display mode. */
  libraryView: "card" | "list";
  /** Right-panel section/row search query (not persisted). */
  search: string;
  /** Auto-open the Picking tab when the viewer selection changes. Off by
   *  default since the panels reserve real width: every click on the model
   *  would otherwise shove the canvas 320px sideways. Selecting now surfaces
   *  the five material panels on the LEFT rail instead, which is both cheaper
   *  and what the reference does. */
  followSelection: boolean;
  /** Per-library "last applied item" id, for active-card highlighting. */
  lastApplied: Record<string, string>;
  /** Post-load normalization applied to dropped/opened model files (v15). */
  dropOptions: { autoCenter: boolean; autoScale: boolean; autoScaleRadius: number };
  /**
   * Basic vs Advanced right rail. Persisted: whichever the user last chose is
   * what they get back. Basic is the default for everyone who has not chosen —
   * a first visit, and existing users once, until they switch.
   */
  inspectorMode: "basic" | "advanced";
  /** The Advanced tab the mode switch returns to. */
  lastAdvancedTab: string;
  /**
   * Rows to flash after "See in Advanced", as full `tab.section.row` paths —
   * one Basic control can move rows in several sections. Session-only (like
   * `search`): a highlight that survived a reload would point at nothing the
   * user just did.
   */
  highlight: { rows: string[]; at: number } | null;
  /**
   * Brand watermark. ONE slice, read by both the on-screen overlay and the
   * export compositor — it used to be DOM chrome only, visible in the editor
   * and present in exactly zero exported pixels while being persisted into
   * render settings as though it affected output.
   *
   * `light` is explicit rather than derived from the background: the Canvas
   * chrome inferred it from whichever "look" was last applied, which is a
   * signal V1 does not have and would have gone stale the moment anyone
   * touched the Background panel.
   */
  watermark: { on: boolean; text: string; light: boolean };
  /**
   * The left panel each mode returns to. Basic and Advanced have different
   * rails (Basic's preset panels vs the reference's 17 libraries), so the one
   * `leftPanel` field is swapped on a mode switch rather than shared.
   */
  lastBasicLeftPanel: string | null;
  lastAdvancedLeftPanel: string | null;
  /** First-run coach marks dismissed. */
  coachDone: boolean;
  /** Advanced: show experimental / debug / duplicate sections. */
  showExperimental: boolean;
  /** Advanced: pinned settings, as `group.section.row` paths (or `group.section` for a whole section). */
  pins: string[];
  /**
   * Present mode — full screen, chrome hidden, the ring spinning for a
   * customer at the counter. Session-only: a reload must never come back with
   * the whole editor hidden.
   */
  present: boolean;
  /**
   * Preview as customer — the studio's chrome steps aside (as in Present) and
   * the shopper's configurator bar shows over the canvas, exactly as the
   * shared link draws it. Session-only for the same reason as `present`, and
   * because leaving it is what puts the design back (`CustomerPreview`).
   */
  customerPreview: boolean;
}

/**
 * Persisted-state generation. Raising it resets the fields listed in `loadUi`
 * and leaves every other saved value alone — which is the whole point: the
 * panel defaults had to change for everyone, but nobody should lose their
 * collapsed sections or last-applied highlights to get that.
 */
const UI_SCHEMA = 6;

export const DEFAULT_UI: UiState = {
  schema: UI_SCHEMA,
  // Both panels start closed and the canvas starts full width, as the
  // reference does. They used to default open, which meant the editor opened
  // with 660px of chrome over a model nobody had looked at yet.
  // The right side opens on Basic mode for a first visit: it is the friendly
  // half, it overlays the canvas rather than shrinking it, and a closed rail of
  // 48px icons gave a new user nothing to start from.
  // v3 (2026-09-29 redesign): Basic opens with its preset panel showing, the
  // way the approved mockup does — a closed rail gave a new user nothing to
  // start from. Scenes (the old Looks) because a whole look is the fastest win.
  leftPanel: "scenes",
  rightTab: "basic",
  lastLeftPanel: "environment",
  lastRightTab: "basic",
  inspectorMode: "basic",
  lastAdvancedTab: "materials",
  highlight: null,
  collapsed: {},
  libraryView: "card",
  viewportLayout: "single",
  activePane: "bottomRight",
  paneModes: { topLeft: "x", topRight: "y", bottomLeft: "z", bottomRight: "perspective" },
  search: "",
  followSelection: false,
  lastApplied: {},
  dropOptions: { autoCenter: false, autoScale: false, autoScaleRadius: 2 },
  watermark: { on: false, text: "", light: false },
  lastBasicLeftPanel: "scenes",
  lastAdvancedLeftPanel: null,
  coachDone: false,
  showExperimental: false,
  pins: [],
  present: false,
  customerPreview: false,
};

const KEY = "editor.ui";

/** Fields we persist across reloads (search is intentionally excluded). */
type PersistedUi = Omit<UiState, "search" | "paneModes" | "highlight" | "present" | "customerPreview">;

export function loadUi(): UiState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_UI };
    const saved = JSON.parse(raw) as Partial<PersistedUi>;
    if ((saved.schema ?? 0) < 3) {
      // Pre-v2 saves pin leftPanel/rightTab to the old open-by-default values,
      // so spreading them would silently defeat the new defaults for every
      // existing user. Drop just those two; keep the rest of their state.
      delete saved.leftPanel;
      delete saved.rightTab;
      // v3: the left rail's ids changed meaning (Basic has its own seven), so
      // an old Advanced panel id is kept as what Advanced returns to.
      if (saved.inspectorMode === "advanced") {
        saved.lastAdvancedLeftPanel = saved.lastLeftPanel ?? null;
        saved.leftPanel = null;
        saved.rightTab = saved.lastAdvancedTab ?? "materials";
      }
    }
    // v4: Advanced's iJewel tabs became our own groups. Re-point the saved tab
    // ids, and carry collapse state over to the new `group.section` keys.
    if ((saved.schema ?? 0) < 4) {
      const map = (t: string | null | undefined) => (t && OLD_TAB_TO_GROUP[t]) || t || null;
      if (saved.rightTab && OLD_TAB_TO_GROUP[saved.rightTab]) saved.rightTab = map(saved.rightTab);
      if (saved.lastRightTab && OLD_TAB_TO_GROUP[saved.lastRightTab]) saved.lastRightTab = map(saved.lastRightTab);
      if (saved.lastAdvancedTab) saved.lastAdvancedTab = map(saved.lastAdvancedTab) ?? "materials";
      if (saved.collapsed) {
        const next: Record<string, boolean> = {};
        for (const [k, v] of Object.entries(saved.collapsed)) {
          const [tab, ...rest] = k.split(".");
          if (tab === "editor") continue;
          const sec = rest.join(".");
          const g = groupOf(sec);
          next[g ? `${g}.${sec}` : k] = v;
        }
        saved.collapsed = next;
      }
    }
    // v5: Basic's rail became the full preset panel and three of its panel ids
    // were renamed (looks → scenes, metal → metals, stones → gems). Re-point a
    // saved one at its successor. Only the retired ids move — the Advanced
    // material panels that share the new names are left exactly as saved.
    if ((saved.schema ?? 0) < 5) {
      for (const k of ["leftPanel", "lastLeftPanel", "lastBasicLeftPanel"] as const) {
        const v = saved[k];
        if (typeof v === "string" && Object.prototype.hasOwnProperty.call(RETIRED_BASIC_PANELS, v)) saved[k] = RETIRED_BASIC_PANELS[v];
      }
    }
    // v6 (2026-10-04):
    //  - `followSelection` was true by default until 2026-09-12 and nothing
    //    ever cleared it or let anyone change it. In Basic it switched
    //    `rightTab` to Advanced's "materials" on every part pick, which blanked
    //    Fine-tune — and `rightTab` is persisted, so it stayed blank. Drop the
    //    flag and give a Basic save its Fine-tune back.
    //  - Fine-tune's cards now start closed. Drop the saved `fineTune.*`
    //    collapse keys once so every existing user starts closed too.
    if ((saved.schema ?? 0) < 6) {
      delete saved.followSelection;
      if (saved.inspectorMode === "basic") {
        saved.rightTab = "basic";
        saved.lastRightTab = "basic";
      }
      if (saved.collapsed) {
        saved.collapsed = Object.fromEntries(Object.entries(saved.collapsed).filter(([k]) => !k.startsWith("fineTune.")));
      }
    }
    // A save from before Basic mode existed has no `inspectorMode`. That user
    // meets Basic mode once (the default), with the panel open so they see it,
    // and the Advanced tab they had becomes what the mode switch returns to.
    // From then on their choice is saved like any other field.
    if (!saved.inspectorMode) {
      const prev = saved.rightTab ?? saved.lastRightTab;
      if (prev && prev !== "basic" && prev !== "ai") saved.lastAdvancedTab = prev;
      saved.rightTab = "basic";
      saved.lastRightTab = "basic";
    }
    // `paneModes` is reset alongside `search`: both are session-only, so an old
    // save that predates the Omit cannot resurrect a three-panes-in-3D layout.
    return {
      ...DEFAULT_UI,
      ...saved,
      schema: UI_SCHEMA,
      search: "",
      highlight: null,
      present: false,
      customerPreview: false,
      paneModes: { ...DEFAULT_UI.paneModes },
    };
  } catch {
    return { ...DEFAULT_UI };
  }
}

export function saveUi(ui: UiState) {
  try {
    const { search: _omit, highlight: _flash, present: _present, customerPreview: _preview, ...persisted } = ui;
    void _omit;
    void _flash;
    void _present;
    void _preview;
    localStorage.setItem(KEY, JSON.stringify(persisted));
  } catch {
    /* storage unavailable (private mode) — non-fatal */
  }
}
